import { useTranslation } from 'react-i18next'
import { Box, Stack, Typography, Link, Button } from '@mui/material'
import { ClientInfo } from '@/features/quotationCreate/ClientInfo'
import { QuotationNoteFormContainer } from '@/features'
import { QuotationDetails, QuotationStatuses } from '@/types'
import { usePostQuotationSendForSignature } from '@/hooks/api/quotations/usePostQuotationSendForSignature'
import { useMutationWrapper } from '@/hooks/useMutationWrapper'
import { useConfirmationModal } from '@/hooks/useConfirmationModal'

type QuotationInfoProps = { quotationDetails: QuotationDetails }

export const QuotationInfo = ({
  quotationDetails,
}: QuotationInfoProps): JSX.Element => {
  const { t } = useTranslation('quotations')
  const { setConfirmationOpen } = useConfirmationModal()
  const { mutateAsync: sendForSignature, isLoading: isSending } =
    useMutationWrapper(() =>
      usePostQuotationSendForSignature(quotationDetails.id)
    )

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <Box width="50%">
        <Typography variant="h6" mb={2}>
          {t('titles.debtor')}
        </Typography>
        <ClientInfo client={quotationDetails.client} />
      </Box>
      <Box width="50%">
        <Typography variant="h6" mb={2}>
          {t('titles.quotation')}
        </Typography>
        <Stack spacing={4}>
          {quotationDetails.project_leader && (
            <Box>
              {t('placeholders.projectLeader')}:&nbsp;
              {`${quotationDetails.project_leader.first_name} ${quotationDetails.project_leader.last_name} [${quotationDetails.project_leader.email}]`}
            </Box>
          )}
          <Box>
            {t('quotationDetails.reference')}:&nbsp;
            {quotationDetails.reference}
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>
              {t('quotationDetails.invoice')}:&nbsp;
              {quotationDetails.invoice_link ||
              quotationDetails.signed_document ? (
                <Link
                  href={
                    quotationDetails.signed_document ??
                    quotationDetails.invoice_link
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary.light"
                >
                  {t('quotationDetails.invoiceLink')}
                </Link>
              ) : (
                '-'
              )}
            </Box>
            {quotationDetails.status === QuotationStatuses.NEW && (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setConfirmationOpen(
                    t('confirmations.sendToClient.title'),
                    t('confirmations.sendToClient.message'),
                    () =>
                      sendForSignature(undefined, {
                        successMessageKey:
                          'quotations:notifications.sentForSignature',
                        errorMessageKey:
                          'quotations:notifications.sendForSignatureFailed',
                      })
                  )
                }
                disabled={isSending}
              >
                {t('buttons.sendToClient')}
              </Button>
            )}
          </Stack>
          <QuotationNoteFormContainer quotationDetails={quotationDetails} />
        </Stack>
      </Box>
    </Stack>
  )
}
